import { Autocomplete, Card } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useFormikContext } from "formik";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import MDInput from "@/components/atoms/MDInput";
import MDTypography from "@/components/atoms/MDTypography";
import { CampaignFormData } from "@/components/screens/CampaignFormPage/types";
import { CampaignFormMessages } from "@/messages/campaigns/campaign-form.messages";
import { CampaignStatus, CampaignType } from "@/types/campaign.types";

import { FORMAT_OPTIONS, statusOptions, typeOptions } from "../../../../constants";
import { handleEndDateChange, handleStartDateChange } from "./helpers";

type Props = {
  messages: CampaignFormMessages;
};

const CampaignInfo = ({ messages }: Props) => {
  const { values, handleChange, errors, setFieldValue } = useFormikContext<CampaignFormData>();
  const { disabledField, status, isActiveStatus } = values;

  const isDiscountEnabled = values.type === CampaignType.DiscountCoupon;

  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Remove espaços e caracteres especiais, mantendo apenas letras e números
    const valueWithoutSpecialChars = event.target.value.replace(/[^a-zA-Z0-9]/g, "");
    setFieldValue("code", valueWithoutSpecialChars);
  };

  const statusValues = statusOptions(messages, values.status).find((opt) => opt.value === status);
  const formatOptions = FORMAT_OPTIONS(messages);

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between">
          <MDTypography variant="h5" fontWeight="bold">
            {messages.campaignInformationTitle}
          </MDTypography>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
              <FormField
                name="code"
                id="campaignCode"
                variant="standard"
                label={messages.campaignCodeLabel}
                onChange={handleCodeChange}
                value={values.code || ""}
                fullWidth
                error={!!errors.code}
                required
                disabled={disabledField || isActiveStatus}
                autoComplete="off"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
              <FormField
                name="name"
                id="campaignName"
                variant="standard"
                label={messages.campaignNameLabel}
                value={values.name || ""}
                onChange={handleChange}
                fullWidth
                error={!!errors.name}
                disabled={disabledField || isActiveStatus}
                required
                autoComplete="off"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
              <Autocomplete
                fullWidth
                value={statusValues || null}
                options={statusOptions(messages, values.status)}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onChange={(_, newValue) => {
                  if (newValue) setFieldValue("status", newValue.value);
                }}
                renderInput={(params) => (
                  <MDInput
                    label={messages.statusLabel}
                    {...params}
                    variant="standard"
                    error={!!errors.status}
                    helperText={errors.status}
                    disabled={disabledField || status === CampaignStatus.Active}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
              <Autocomplete
                fullWidth
                options={formatOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                value={formatOptions.find((option) => option.value === values.format) || null}
                onChange={(_, newValue) => {
                  setFieldValue("format", newValue ? newValue.value : "");
                }}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    label={messages.formatLabel}
                    variant="standard"
                    error={!!errors.format}
                    helperText={errors.format}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
              <Autocomplete
                fullWidth
                value={typeOptions(messages).find((option) => option.value === values.type) || null}
                options={typeOptions(messages)}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onChange={(_, newValue) => {
                  if (newValue) {
                    setFieldValue("type", newValue.value);
                    if (newValue.value !== CampaignType.DiscountCoupon) {
                      setFieldValue("discountPercentage", 0.0);
                    }
                  }
                }}
                renderInput={(params) => (
                  <MDInput
                    label={messages.campaignTypeLabel}
                    {...params}
                    variant="standard"
                    error={!!errors.type}
                    helperText={errors.type}
                    disabled={disabledField || isActiveStatus}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
              <FormField
                fullWidth
                name="discountPercentage"
                id="discountPercentage"
                type="number"
                variant="standard"
                label={messages.discountPercentageLabel}
                value={values.discountPercentage?.toString() || ""}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                error={!!errors.discountPercentage}
                disabled={!isDiscountEnabled || disabledField}
                autoComplete="off"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
              <DatePicker
                label={messages.startValidityDateLabel}
                value={dayjs(values.startDate, "YYYY-MM-DDTHH:mm:ss")}
                onChange={(value) => {
                  const formattedStartDate = handleStartDateChange(value);
                  setFieldValue("startDate", formattedStartDate);
                }}
                minDate={dayjs()}
                disabled={disabledField || isActiveStatus}
                slotProps={{
                  textField: {
                    helperText: errors.startDate,
                    error: Boolean(errors.startDate),
                    fullWidth: true,
                    variant: "standard",
                    disabled: disabledField || isActiveStatus,
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
              <DatePicker
                label={messages.endValidityDateLabel}
                value={dayjs(values.endDate, "YYYY-MM-DDTHH:mm:ss")}
                onChange={(value) => {
                  const formattedEndDate = handleEndDateChange(value, values.startDate);
                  setFieldValue("endDate", formattedEndDate);
                }}
                minDate={dayjs()}
                disabled={values.disabledField}
                slotProps={{
                  textField: {
                    helperText: errors.endDate,
                    error: Boolean(errors.endDate),
                    fullWidth: true,
                    variant: "standard",
                    disabled: values.disabledField,
                  },
                }}
              />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </Card>
  );
};

export default CampaignInfo;
